import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { businessDate } from '@evogard/domain';
import { Calendar } from './Calendar.js';
import {
  addSpan,
  isSelected,
  parseSelection,
  removeSpan,
  selectionSummary,
  spanLabel,
  toggleUnit,
  unitLabel,
  type CalendarUnit,
} from './calendarSelection.js';
import { cn } from './cn.js';
import { useLayer } from './layer.js';
import { Icon } from './Icon.js';
import { Popover, PopoverContent, PopoverTrigger } from './primitives/popover.js';

export interface CalendarSelectProps {
  label: string;
  /** `month` escolhe competências (`AAAA-MM`); `day`, datas (`AAAA-MM-DD`). */
  unit: CalendarUnit;
  /**
   * A seleção, no formato de `calendarSelection.ts`: unidade solta
   * (`2026-03`) ou faixa fechada (`2026-01/2026-06`).
   */
  selected: readonly string[];
  /** Recebe a lista SEMPRE canônica — ordenada, sem sobreposição. */
  onChange: (selected: string[]) => void;
  /** O texto do gatilho quando nada está marcado — "Todas", "Todos". */
  placeholder?: string;
  hiddenLabel?: boolean;
  disabled?: boolean;
  /** A camada da lista — o mesmo `layer` do `MultiSelect`. */
  layer?: 'painel' | 'modal';
}

type Mode = 'avulsos' | 'faixa';

/**
 * Vários meses, ou vários dias, escolhidos olhando o calendário — soltos ou
 * em faixa, no mesmo seletor.
 *
 * ## Dois modos, e não `Shift`+clique
 *
 * A faixa por `Shift`+clique é o atalho de quem usa planilha, e não existe no
 * dedo: o telefone não tem `Shift`. Com o modo declarado no alto, os dois
 * gestos são um toque — "Avulsos" liga e desliga a unidade tocada; "Faixa"
 * pede duas pontas, e a primeira fica marcada esperando a segunda (tocar nela
 * de novo desiste).
 *
 * ## A lista do que está escolhido mora DENTRO da camada
 *
 * O gatilho resume (`selectionSummary`), pelo motivo do `MultiSelect`: o
 * painel de filtros tem trilhas de altura fixa. Mas ao contrário de lá, aqui o
 * calendário mostra só um ano (ou um mês) por vez, e uma faixa que atravessa o
 * ano ficaria meio invisível. Por isso a camada lista cada item, por extenso,
 * com o "×" que o tira inteiro.
 *
 * ## O que este componente NÃO decide
 *
 * O que a rota faz com a lista. Somar meses numa apuração, numa comissão ou
 * num extrato é REGRA (regra 14), e nenhuma tela de competência da casa
 * aceita mais de um mês em 08/10/2026 — elas continuam no `PeriodField`.
 */
export function CalendarSelect({
  label,
  unit,
  selected,
  onChange,
  placeholder = 'Todas',
  hiddenLabel = false,
  disabled = false,
  layer: layerProp,
}: CalendarSelectProps) {
  const layer = useLayer(layerProp);
  const idLabel = useId();
  const idTrigger = useId();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('avulsos');
  /** A primeira ponta da faixa, esperando a segunda. */
  const [anchor, setAnchor] = useState<string | null>(null);

  const spans = parseSelection(selected, unit);
  const summary = selectionSummary(selected, unit, placeholder);

  const pick = (value: string) => {
    if (mode === 'avulsos') {
      onChange(toggleUnit(selected, value, unit));
      return;
    }
    if (anchor === null) {
      setAnchor(value);
    } else if (anchor === value) {
      setAnchor(null);
    } else {
      onChange(addSpan(selected, anchor, value, unit));
      setAnchor(null);
    }
  };

  const marked = (value: string) => value === anchor || isSelected(selected, value, unit);

  return (
    <div className="flex flex-col gap-1.5">
      <span
        id={idLabel}
        className={cn('text-xs font-medium text-texto', hiddenLabel && 'sr-only')}
      >
        {label}
      </span>

      <Popover
        modal={layer === 'modal'}
        open={open}
        onOpenChange={(next) => {
          if (disabled) return;
          setOpen(next);
          if (!next) setAnchor(null);
        }}
      >
        <PopoverTrigger asChild>
          <button
            /* Veste um campo: o foco dele é o de dentro, não o anel do botão. */
            data-campo=""
            type="button"
            id={idTrigger}
            disabled={disabled}
            aria-labelledby={`${idLabel} ${idTrigger}`}
            className={cn(
              'flex h-9 w-full items-center justify-between gap-2 rounded-borda border bg-superficie px-3 text-xs',
              'text-texto pointer-coarse:h-11',
              'transition-[border-color,background-color] active:bg-superficie-suave',
              'border-borda-forte hover:border-texto-suave',
              'disabled:cursor-not-allowed disabled:bg-superficie-suave disabled:opacity-60',
            )}
          >
            <span
              className={cn(
                'truncate text-left',
                spans.length === 0 && 'text-texto-suave',
              )}
            >
              {summary}
            </span>
            <Icon nome="calendario" size={16} className="shrink-0 text-texto-suave" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          /* O `z` do `MultiSelect`, com o mesmo raciocínio — ver lá. */
          className={cn(
            'flex w-auto max-w-[calc(100vw-2rem)] flex-col gap-3 p-3',
            layer === 'modal' ? 'z-50' : 'z-10',
          )}
          onOpenAutoFocus={(e) => {
            /* Quem põe o foco é a grade — ver `Calendar`. */
            e.preventDefault();
          }}
        >
          <div
            role="radiogroup"
            aria-label="Como escolher"
            className="grid grid-cols-2 gap-1 rounded-borda bg-superficie-suave p-1"
          >
            {(
              [
                ['avulsos', unit === 'month' ? 'Meses avulsos' : 'Dias avulsos'],
                ['faixa', 'Faixa'],
              ] as const
            ).map(([value, text]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={mode === value}
                onClick={() => {
                  setMode(value);
                  setAnchor(null);
                }}
                className={cn(
                  'h-8 rounded-borda px-2 text-xs font-semibold pointer-coarse:h-11',
                  mode === value
                    ? 'bg-superficie text-texto shadow-sm'
                    : 'text-texto-suave hover:text-texto',
                )}
              >
                {text}
              </button>
            ))}
          </div>

          {/* O que o próximo toque faz — sem isto, a primeira ponta marcada
           * parece uma unidade solta escolhida. */}
          <p aria-live="polite" className="text-rotulo text-texto-suave">
            {mode === 'avulsos'
              ? 'Toque para marcar ou desmarcar.'
              : anchor === null
                ? 'Toque no início da faixa.'
                : `Início em ${unitLabel(anchor, unit)}. Toque no fim.`}
          </p>

          {unit === 'month' ? (
            <MonthGrid onSelect={pick} isMarked={marked} autoFocus />
          ) : (
            <Calendar value="" onSelect={pick} isMarked={marked} autoFocus />
          )}

          {spans.length > 0 && (
            <div className="flex flex-col gap-1 border-t border-borda pt-2">
              <ul
                aria-label="Escolhidos"
                className="flex max-h-32 flex-col overflow-y-auto"
              >
                {spans.map((span) => {
                  const text = spanLabel(span, unit);
                  return (
                    <li
                      key={`${span.from}/${span.to}`}
                      className="flex items-center justify-between gap-2 rounded-borda px-2 text-xs text-texto hover:bg-superficie-suave"
                    >
                      <span className="tabular-nums">{text}</span>
                      <button
                        type="button"
                        aria-label={`Tirar ${text}`}
                        title="Tirar"
                        onClick={() => onChange(removeSpan(selected, span, unit))}
                        className="flex h-7 w-7 items-center justify-center rounded-borda text-texto-suave hover:text-texto active:bg-borda pointer-coarse:h-11 pointer-coarse:w-11"
                      >
                        <Icon nome="fechar" size={16} />
                      </button>
                    </li>
                  );
                })}
              </ul>
              <button
                type="button"
                onClick={() => {
                  onChange([]);
                  setAnchor(null);
                }}
                className="w-full rounded-borda px-2 py-1.5 text-center text-xs font-semibold text-texto-suave hover:bg-superficie-suave hover:text-texto"
              >
                Limpar seleção
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

const MONTH_NAMES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

/**
 * O ano em doze botões, para escolher competência.
 *
 * O teclado segue o `Calendar` (o padrão de grade da APG): um mês tabulável
 * por vez, setas andam um mês ou uma fileira de três, `PageUp`/`PageDown`
 * trocam de ano. O ano acompanha o foco.
 */
function MonthGrid({
  onSelect,
  isMarked,
  autoFocus = false,
}: {
  onSelect: (value: string) => void;
  isMarked: (value: string) => boolean;
  autoFocus?: boolean;
}) {
  const current = businessDate(new Date()).slice(0, 7);
  const [cursor, setCursor] = useState(current);
  const gridRef = useRef<HTMLDivElement>(null);
  const follow = useRef(autoFocus);

  const year = Number(cursor.slice(0, 4));
  const shift = (value: string, n: number) => {
    const total = Number(value.slice(0, 4)) * 12 + Number(value.slice(5, 7)) - 1 + n;
    return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!follow.current) return;
    follow.current = false;
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-mes="${cursor}"]`)?.focus();
  }, [cursor]);

  const move = (next: string) => {
    follow.current = true;
    setCursor(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -3,
      ArrowDown: 3,
      PageUp: -12,
      PageDown: 12,
    };
    const n = step[e.key];
    if (n === undefined) return;
    e.preventDefault();
    move(shift(cursor, n));
  };

  return (
    <div className="flex w-max flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <NavButton
          label="Ano anterior"
          icon="seta-esquerda"
          onClick={() => setCursor(shift(cursor, -12))}
        />
        <p aria-live="polite" className="text-sm font-semibold tabular-nums text-texto">
          {year}
        </p>
        <NavButton
          label="Próximo ano"
          icon="seta-direita"
          onClick={() => setCursor(shift(cursor, 12))}
        />
      </div>

      <div
        ref={gridRef}
        role="grid"
        aria-label={String(year)}
        onKeyDown={onKeyDown}
        className="flex flex-col gap-1"
      >
        {[0, 3, 6, 9].map((row) => (
          <div key={row} role="row" className="grid grid-cols-3 gap-1">
            {MONTH_NAMES.slice(row, row + 3).map((name, j) => {
              const i = row + j;
              const value = `${year}-${String(i + 1).padStart(2, '0')}`;
              const marked = isMarked(value);
              const isCurrent = value === current;
              return (
                <span key={value} role="gridcell" aria-selected={marked}>
                  <button
                    type="button"
                    data-mes={value}
                    tabIndex={value === cursor ? 0 : -1}
                    aria-label={`${name} de ${year}`}
                    aria-current={isCurrent ? 'date' : undefined}
                    onClick={() => onSelect(value)}
                    onFocus={() => value !== cursor && setCursor(value)}
                    className={cn(
                      'flex h-9 w-16 items-center justify-center rounded-borda text-xs capitalize pointer-coarse:h-11',
                      'transition-colors',
                      marked
                        ? 'bg-primaria font-bold text-primaria-contraste'
                        : cn(
                            'text-texto hover:bg-superficie-suave active:bg-borda',
                            isCurrent &&
                              'font-bold text-primaria-texto ring-1 ring-inset ring-primaria-texto',
                          ),
                    )}
                  >
                    {name.slice(0, 3)}
                  </button>
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function NavButton({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: 'seta-esquerda' | 'seta-direita';
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="flex h-8 w-8 items-center justify-center rounded-borda text-texto-suave hover:bg-superficie-suave hover:text-texto active:bg-borda pointer-coarse:h-11 pointer-coarse:w-11"
    >
      <Icon nome={icon} size={16} />
    </button>
  );
}
